import type { VoidFunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import { Alert, Button, DropdownButton, MenuItem } from 'react-bootstrap';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { orgRolesList, roleLabel } from '../../helpers/role';
import { useToggle } from '../../hooks';
import type { RolesStore } from '../../redux/roles';
import RolesModule from '../../redux/roles';
import type { ReduxStore } from '../../redux/store';
import UsersModule, { type UsersStore } from '../../redux/users';
import type { User } from '../../types';
import { hasError } from '../../utils';
import { ErrorAlert, FormLabel, LabeledCheckbox, LabeledTextInput } from '../generic';
import Flex from '../generic/Flex';
import Text from '../generic/Text';

type ReduxStateProps = {
  roles: RolesStore['roles'];
  rolesStatus: RolesStore['status'];
  usersStatus: UsersStore['status'];
};

type DispatchProps = {
  fetchRoles(): Promise<void>;
  createUser(user: User): Promise<void>;
  updateUserRole(userId: number, roleId: number | null): Promise<void>;
};

type Props = ReduxStateProps & DispatchProps;

const CreateApiUserCard = (props: Props) => {
  const [username, setUsername] = useState<string>('');
  const [roleId, setRoleId] = useState<number | null>(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [createPushOnlyApiToken, toggleCreatePushOnlyApiToken] = useToggle(true);
  const [createFullApiToken, toggleCreateFullApiToken] = useToggle(true);

  // On mount, fetch all roles.
  useEffect(() => {
    // No need to await.
    props.fetchRoles();
  }, []);

  const onCreateUser = async () => {
    try {
      const { value } = await props.createUser({
        username,
        // role here has nothing to do with permissions but is just metadata about the user's role in their org.
        role: 'API user',
        createPushOnlyApiToken,
        createFullApiToken,
      });

      // If the new user's role isn't the role that was selected, update the user's role.
      if (
        value?.data &&
        ((value.data as User).userRole?.roleId || null) !== roleId
      ) {
        await props.updateUserRole(value.data.id, roleId);
      }

      setUsername('');
      setShowSuccessMessage(true);

      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 2500);
    } catch (e) {
      // Error handled by Redux.
    }
  };

  return (
    <div className="card">
      {showSuccessMessage && (
        <Alert bsStyle="success">New user created</Alert>
      )}
      {hasError(props.rolesStatus) && (
        <ErrorAlert status={props.rolesStatus} />
      )}
      {hasError(props.usersStatus) && (
        <ErrorAlert status={props.usersStatus} />
      )}
      <Text.H4>Create an API User</Text.H4>
      <div className="margin-bottom-md">
        <Text.Text>
          Create a special user to have an API key that is unaffected by the organization&rsquo;s SSO configuration.
        </Text.Text>
      </div>
      <LabeledTextInput
        label="Username"
        value={username}
        onChange={setUsername}
      />
      <FormLabel
        label="Role"
        htmlFor="new-user-org-role-dropdown"
      >
        <DropdownButton
          id="new-user-org-role-dropdown"
          title={roleLabel(props.roles, roleId)}
          onSelect={setRoleId}
          bsStyle="default"
        >
          {orgRolesList(props.roles).map(role => (
            <MenuItem
              key={role.id || 0}
              eventKey={role.id}
              active={roleId === role.id}
              name="roleId">
              {role.name}
            </MenuItem>
          ))}
        </DropdownButton>
      </FormLabel>
      <LabeledCheckbox
        label="Create a push-only API token"
        isChecked={createPushOnlyApiToken}
        onChange={toggleCreatePushOnlyApiToken}
      />
      <br />
      <LabeledCheckbox
        label="Create a full API token"
        isChecked={createFullApiToken}
        onChange={toggleCreateFullApiToken}
      />
      <Flex justify="flex-end">
        <Button
          bsStyle="primary"
          onClick={onCreateUser}
          disabled={props.usersStatus.loading || !username.trim()}
        >
          Create
        </Button>
      </Flex>
    </div>
  );
};

const mapStateToProps = createStructuredSelector<ReduxStore, ReduxStateProps>({
  roles: RolesModule.selectors.getRoles,
  rolesStatus: RolesModule.selectors.getStatus,
  usersStatus: UsersModule.selectors.getStatus,
});

const mapDispatchToProps: DispatchProps = {
  fetchRoles: RolesModule.actions.fetchAll,
  createUser: UsersModule.actions.create,
  updateUserRole: UsersModule.actions.updatePermission,
};

export default (connect(mapStateToProps, mapDispatchToProps)(CreateApiUserCard) as VoidFunctionComponent);
